import { useMemo, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import type { PreviewEnvelope } from '../../types';
import { Eyebrow } from '../Eyebrow';
import { BestellungReviewScreen } from './BestellungReviewScreen';
import { BuchartTiles } from './BuchartTiles';
import { FaqAccordion } from './FaqAccordion';

/**
 * Bildschirm 5 · Bestellung — Block 6 rewrite (Sebastian ruling
 * 2026-09-09). The flow drops from three screens to two: tile
 * selection (here) → Bestellübersicht (BestellungReviewScreen,
 * unchanged from the 2026-09-09 slice) → Stripe. The pre-Block-6
 * intermediate price-summary + two-question flow (Q1 Buchart /
 * Q2 Format via BuchartCards + FormatCards + AnsweredCollapse) is
 * RETIRED. Those primitives stay in the tree for their tests but
 * are no longer consumed by this screen.
 *
 * Layout (Block 6):
 *   Eyebrow "EURE BESTELLUNG"
 *   H1 "Wie soll euer Buch aussehen?"
 *   Return-from-Stripe banner (conditional)
 *   Outcome banner (conditional)
 *   BuchartTiles — three fixed tiles (Hardcover / Layflat /
 *     Pappbuch-disabled) with prices + descriptions
 *   Gold "Weiter zur Bestellübersicht" button (disabled until pick)
 *   FAQ heading "Häufige Fragen"
 *   FaqAccordion (4 questions post-Block-6.4 rework)
 *
 * NO Q1/Q2 flow. NO price summary (the tile carries the price;
 * the review screen carries the total with shipping). NO
 * AnsweredCollapse. Reload-safe URL params still work — the
 * parent hydrates `initialSelectedSlug` for review-phase reloads.
 */
export interface BestellungScreenProps {
  readonly envelope: PreviewEnvelope;
  readonly initialPhase?: 'select' | 'review';
  readonly initialSelectedSlug?: string;
  readonly onEnterReview?: (slug: string) => void;
  readonly onExitReview?: () => void;
  /**
   * Book-funnel analytics — event 8 (`book_checkout_started`).
   * Forwarded to `BestellungReviewScreen`, which invokes it inside
   * `handleKaufen` BEFORE the `createBookCheckout` request goes out.
   * The route owner (BestellungRoute) is the fire site; this prop is
   * a pure wire.
   */
  readonly onCheckoutStart?: (slug: string | null) => void;
  /**
   * Sebastian ruling 2026-09-25 (E2E-test case D). Forwarded to
   * `BestellungReviewScreen` and invoked ONLY on the balance-only
   * branch (`paidWithBalance === true`) right AFTER the successful
   * `createBookCheckout` POST. `BestellungRoute` wires this to the
   * shell context's `refetch`.
   */
  readonly onPaidWithBalance?: () => void;
}

export function BestellungScreen({
  envelope,
  initialPhase = 'select',
  initialSelectedSlug,
  onEnterReview,
  onExitReview,
  onCheckoutStart,
  onPaidWithBalance,
}: BestellungScreenProps) {
  const { orderableSalesUnits } = envelope;

  // Reload-safe seed for the URL-param path. Same discipline as
  // pre-Block-6: parent validates + hydrates initialSelectedSlug,
  // this screen defends by falling back to null on a stale slug.
  const initialUnitMatch = useMemo(
    () =>
      initialSelectedSlug === undefined
        ? null
        : (orderableSalesUnits.find((u) => u.slug === initialSelectedSlug) ?? null),
    [initialSelectedSlug, orderableSalesUnits],
  );

  const [selectedSlug, setSelectedSlug] = useState<string | null>(
    () => initialUnitMatch?.slug ?? null,
  );

  const [phase, setPhase] = useState<'select' | 'review'>(() =>
    initialPhase === 'review' && initialUnitMatch !== null ? 'review' : 'select',
  );

  // Banner precedence (Sebastian ruling 2026-09-09, unchanged pre-
  // Block-6): return-from-Stripe wins over outcome banner while a
  // session is live.
  const sessionIsLive = envelope.status === 'checkout_pending';
  const showReturnBanner = sessionIsLive;
  const outcomeBannerCopy = computeOutcomeBannerCopy({
    outcome: envelope.lastCheckoutOutcome,
    at: envelope.lastCheckoutOutcomeAt,
    sessionIsLive,
    now: Date.now(),
  });

  const selectedUnit = useMemo(
    () =>
      selectedSlug === null
        ? null
        : (orderableSalesUnits.find((u) => u.slug === selectedSlug) ?? null),
    [selectedSlug, orderableSalesUnits],
  );

  const handleWeiter = () => {
    if (selectedSlug === null) return;
    setPhase('review');
    onEnterReview?.(selectedSlug);
  };

  const handleReviewBack = () => {
    setPhase('select');
    onExitReview?.();
  };

  // Phase 'review' — the Bestellübersicht acknowledgment screen.
  if (phase === 'review' && selectedUnit !== null) {
    return (
      <BestellungReviewScreen
        envelope={envelope}
        selectedUnit={selectedUnit}
        onBack={handleReviewBack}
        {...(onCheckoutStart !== undefined ? { onCheckoutStart } : {})}
        {...(onPaidWithBalance !== undefined ? { onPaidWithBalance } : {})}
      />
    );
  }

  return (
    <div className="space-y-6 py-4">
      {/* Block 6 · 6.1 · Eyebrow + heading */}
      <div className="flex flex-col gap-3">
        <Eyebrow text={STRINGS.book.bestellung.eyebrow} />
        <h1 className="text-2xl font-semibold text-dream-navy">
          {STRINGS.book.bestellung.heading}
        </h1>
      </div>

      {showReturnBanner && (
        <p className="rounded-lg bg-dream-background px-4 py-3 text-sm text-dream-navy">
          {STRINGS.book.bestellung.returnFromCheckoutBanner}
        </p>
      )}

      {outcomeBannerCopy !== null && (
        <p className="whitespace-pre-line rounded-lg bg-dream-background px-4 py-3 text-sm text-dream-navy">
          {outcomeBannerCopy}
        </p>
      )}

      {/* Block 6 · 6.2 · Three fixed tiles */}
      <BuchartTiles
        orderableSalesUnits={orderableSalesUnits}
        selectedSlug={selectedSlug}
        onPick={(slug) => setSelectedSlug(slug)}
        shippingCents={envelope.shippingCents}
      />

      {/* Block 6 · 6.3 · Gold Weiter — "Weiter zur Bestellübersicht" */}
      <div className="pt-2">
        <button
          type="button"
          onClick={handleWeiter}
          disabled={selectedSlug === null}
          className="h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60"
        >
          {STRINGS.book.bestellung.weiter}
        </button>
      </div>

      {/* Block 6 · 6.4 · FAQ section with bold heading + rebuilt
          accordion. */}
      <section className="space-y-3 pt-4">
        <h2 className="text-lg font-semibold text-dream-navy">
          {STRINGS.book.bestellung.faqHeading}
        </h2>
        {/*
          Sebastian ruling 2026-09-25 (Webflow shipping-price
          update). The FAQ answer for "Wie groß ist das Buch?"
          mentions the shipping charge; source it from the
          envelope so a future config change updates the FAQ line
          automatically. Same discipline as `tileShippingHint`.
        */}
        <FaqAccordion shippingCents={envelope.shippingCents} />
      </section>
    </div>
  );
}

/**
 * Outcome-banner copy selector — unchanged from the pre-Block-6
 * shape (Sebastian ruling 2026-09-09). Exported for the spec.
 */
export function computeOutcomeBannerCopy(input: {
  readonly outcome: 'declined' | 'expired' | null;
  readonly at: string | null;
  readonly sessionIsLive: boolean;
  readonly now: number;
}): string | null {
  if (input.outcome === null) return null;
  if (input.sessionIsLive) return null;
  if (input.at === null) return null;
  const atMs = Date.parse(input.at);
  if (!Number.isFinite(atMs)) return null;
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  if (input.now - atMs > SEVEN_DAYS_MS) return null;
  return input.outcome === 'declined'
    ? STRINGS.book.bestellung.checkoutDeclinedBanner
    : STRINGS.book.bestellung.checkoutExpiredBanner;
}
